import { FC } from "react";
import styled from "styled-components";
import { color } from "../../../styles";
import { MobileOnly, mobile, tablet } from "components/templates/Breakpoints";
import Link from "next/link";
import { PillComponent } from "./PillComponent";
import Image from "next/image";
import { getLinkFromContentType } from "@utils";
import { Truncator } from "components/atoms/Truncator";

export interface ResourceCardProps {
  thumbnailSrc?: string;
  thumbnailAlt?: string;
  tag?: string;
  category?: string;
  duration?: string;
  title: string;
  truncation?: number;
  excerpt?: string;
  contentType: string;
  slug: string;
  isHorizontal?: boolean;
  imgHeight?: string;
  imgWidth?: string;
  imgMargin?: string;
  pillPosition?: "bottom" | "top";
  priority?: boolean;
  nextImageHeight?: number;
  nextImageWidth?: number;
}

export const BasicResourceCard: FC<ResourceCardProps> = ({
  thumbnailSrc,
  thumbnailAlt,
  tag,
  category,
  duration,
  title,
  truncation = 3,
  excerpt,
  slug,
  contentType,
  imgHeight,
  imgWidth,
  nextImageHeight,
  nextImageWidth,
  imgMargin,
  isHorizontal,
  pillPosition,
  priority = false,
}) => {
  return (
    <Link href={getLinkFromContentType(contentType, slug)}>
      <Container>
        <ImageContentWrapper $isHorizontal={isHorizontal && !!thumbnailSrc}>
          {thumbnailSrc ? (
            <CardImage
              src={thumbnailSrc}
              alt={thumbnailAlt || ""}
              $imgHeight={imgHeight}
              $imgWidth={imgWidth}
              $imgMargin={imgMargin}
              height={nextImageHeight || 190}
              width={nextImageWidth || 348}
              quality={100}
              priority={priority}
            />
          ) : null}
          <Flex $isHorizontal={isHorizontal}>
            {pillPosition === "top" && (
              <PillComponent tag={tag} category={category} duration={duration} pb="1.5rem" />
            )}

            <Title>
              <Truncator $lines={truncation}>{title}</Truncator>
            </Title>
            {excerpt ? (
              <Excerpt>
                <Truncator $lines={truncation}>{excerpt}</Truncator>
              </Excerpt>
            ) : null}
            {pillPosition === "bottom" && (
              <PillComponent tag={tag} category={category} duration={duration} pt="1rem" />
            )}
          </Flex>
        </ImageContentWrapper>
      </Container>
    </Link>
  );
};

const Container = styled.div`
  max-width: 100%;
  background-color: ${color.white};
  height: 100%;
`;

const ImageContentWrapper = styled.div<{ $isHorizontal?: boolean }>`
  ${(p) =>
    p.$isHorizontal
      ? ` 
    display: flex;
    column-gap: 2.5rem;
    height: 100%;

    ${mobile} {
      column-gap: 1rem;
    }
  `
      : `grid-template-columns: 1fr;`}
`;

const CardImage = styled(Image)<{ $imgHeight?: string; $imgMargin?: string; $imgWidth?: string }>`
  border: 1px solid ${color.B240};
  border-radius: 0.5rem;
  height: ${(p) => p.$imgHeight || ""};
  width: ${(p) => p.$imgWidth || "100%"};
  object-fit: cover;
  margin-bottom: ${(p) => p.$imgMargin};
  max-height: 30rem;
  box-shadow: 0.25rem 0.25rem 0 ${color.B220};
  transition: 150ms ease-out;

  &:hover {
    transform: translateY(-0.5rem);
    box-shadow: 0.5rem 0.5rem 0 ${color.B220};
  }
`;

const Flex = styled.div<{ $isHorizontal: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: ${(p) => (p.$isHorizontal ? "space-between" : "")};

  @media only screen and (min-width: 90rem) {
    justify-content: flex-start;
  }

  ${mobile} {
    min-width: ${(p) => (p.$isHorizontal ? "9.375rem" : "")};
  }
`;

const Title = styled.p`
  overflow: hidden;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 700;
  line-height: 1.75rem;
  letter-spacing: 0.075rem;
  color: ${color.B1100};
  padding: 0;

  @media only screen and (max-width: 80rem) {
    font-size: 1.25rem;
    line-height: 1.8rem;
  }

  ${tablet} {
    font-size: 1rem;
    line-height: 1.5rem;
    letter-spacing: 0.075rem;
  }
`;

const Excerpt = styled.p`
  font-size: 1.25rem;
  line-height: 2rem;
  letter-spacing: 0.075rem;
  padding-top: 0.75rem;
  color: ${color.B1100};

  ${tablet} {
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;
